import { useState, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  Linking,
} from 'react-native';
import { useRouter } from 'expo-router';
import { supabase } from '@/lib/supabase';
import { Lock, CheckCircle, Eye, EyeOff, AlertCircle, Smartphone } from 'lucide-react-native';

import { useTheme } from '@/contexts/ThemeContext';

const log = createLogger('RESET_PW');

export default function ResetPasswordScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifyingSession, setVerifyingSession] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isValidRecoverySession, setIsValidRecoverySession] = useState(false);

  useEffect(() => {
    verifyRecoverySession();
  }, []);

  const verifyRecoverySession = async () => {
    log.info('[RESET_PASSWORD] Verifying recovery session');

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      log.info('[RESET_PASSWORD] Session check:', {
        hasSession: !!session,
        hasError: !!sessionError,
        userId: session?.user?.id,
        userAud: session?.user?.aud,
        recoverySentAt: session?.user?.recovery_sent_at,
      });

      if (sessionError) {
        log.error('[RESET_PASSWORD] Session error:', {
          message: sessionError.message,
          fullError: sessionError,
        });
        setError('Your session has expired. Please request a new password reset link.');
        setIsValidRecoverySession(false);
        setVerifyingSession(false);
        return;
      }

      if (!session) {
        log.error('[RESET_PASSWORD] No active session found');
        setError('No active recovery session. Please request a new password reset link.');
        setIsValidRecoverySession(false);
        setVerifyingSession(false);
        return;
      }

      const user = session.user;
      const isRecovery = user?.aud === 'authenticated' && user?.recovery_sent_at !== undefined;

      log.info('[RESET_PASSWORD] Recovery validation:', {
        isRecovery,
        aud: user?.aud,
        hasRecoverySentAt: !!user?.recovery_sent_at,
      });

      if (!isRecovery) {
        log.error('[RESET_PASSWORD] Session is not a password recovery session');
        setError('Invalid recovery session. Please request a new password reset link.');
        setIsValidRecoverySession(false);
      } else {
        log.info('[RESET_PASSWORD] Valid recovery session confirmed');
        setIsValidRecoverySession(true);
      }
    } catch (err: any) {
      log.error('[RESET_PASSWORD] Verification error:', {
        message: err.message,
        fullError: err,
      });
      setError('Something went wrong. Please request a new password reset link.');
      setIsValidRecoverySession(false);
    } finally {
      setVerifyingSession(false);
    }
  };

  const handleOpenInApp = async () => {
    try {
      const deepLinkUrl = 'duel://reset-password';
      const canOpen = await Linking.canOpenURL(deepLinkUrl);

      if (canOpen) {
        await Linking.openURL(deepLinkUrl);
      } else {
        setError('DUEL app is not installed or deep linking is not supported on this device.');
      }
    } catch (err: any) {
      log.error('[RESET_PASSWORD] Failed to open deep link:', err);
      setError('Failed to open DUEL app. Please make sure the app is installed.');
    }
  };

  const handleResetPassword = async () => {
    if (!isValidRecoverySession) {
      setError('Invalid recovery session. Please request a new password reset link.');
      return;
    }
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
      setError('Password must contain at least one letter and one number.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    log.info('[RESET_PASSWORD] Attempting password update');

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        log.error('[RESET_PASSWORD] Update error:', {
          message: updateError.message,
          status: updateError.status,
          name: updateError.name,
          fullError: updateError,
        });

        if (updateError.message.includes('same as')) {
          throw new Error('New password must be different from your current password.');
        }
        throw new Error('Something went wrong. Please try again.');
      }

      log.info('[RESET_PASSWORD] Password updated successfully');

      await supabase.auth.signOut();
      log.info('[RESET_PASSWORD] User signed out, recovery state cleared');

      setSuccess(true);

      setTimeout(() => {
        router.replace('/(auth)/signin');
      }, 3000);
    } catch (err: any) {
      log.error('[RESET_PASSWORD] Update error:', {
        message: err.message,
        fullError: err,
      });
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifyingSession) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.successContainer}>
          <ActivityIndicator size="large" color={theme.colors.accent} />
          <Text style={[styles.loadingText, { color: theme.colors.textSecondary }]}>Verifying recovery session...</Text>
        </View>
      </View>
    );
  }

  if (!isValidRecoverySession && error) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.successContainer}>
          <View style={[styles.errorIconContainer, { backgroundColor: theme.colors.surface, borderColor: theme.colors.accent }]}>
            <AlertCircle size={64} color={theme.colors.accent} />
          </View>
          <Text style={[styles.errorTitle, { color: theme.colors.text }]}>Invalid or Expired Link</Text>
          <Text style={[styles.errorMessage, { color: theme.colors.accent }]}>{error}</Text>

          <View style={[styles.fallbackInstructions, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
            <Text style={[styles.fallbackTitle, { color: theme.colors.text }]}>What to do:</Text>
            <Text style={[styles.fallbackText, { color: theme.colors.textSecondary }]}>
              1. Request a new password reset email{'\n'}
              2. Check your email (including spam folder){'\n'}
              3. Click the link within 60 minutes{'\n'}
              {Platform.OS !== 'web' && '4. Link must open in this app'}
            </Text>
          </View>

          {Platform.OS === 'web' && (
            <>
              <View style={[styles.mobileHandoffBox, { backgroundColor: theme.colors.surface, borderColor: '#4ADE80' }]}>
                <View style={styles.mobileHandoffHeader}>
                  <Smartphone size={20} color="#4ADE80" />
                  <Text style={styles.mobileHandoffTitle}>Using Mobile?</Text>
                </View>
                <Text style={[styles.mobileHandoffText, { color: theme.colors.text }]}>
                  If you received the reset email on mobile and have the DUEL app installed,
                  tap the button below to open it directly in the app.
                </Text>
              </View>

              <TouchableOpacity style={[styles.appLinkButton, { backgroundColor: '#4ADE80' }]} onPress={handleOpenInApp}>
                <Smartphone size={20} color={theme.colors.text} />
                <Text style={[styles.buttonText, { color: theme.colors.accentForeground }]}>Open in DUEL App</Text>
              </TouchableOpacity>
            </>
          )}

          <TouchableOpacity
            style={[styles.button, { backgroundColor: theme.colors.accent }]}
            onPress={() => router.replace('/(auth)/forgot-password')}>
            <Text style={[styles.buttonText, { color: theme.colors.accentForeground }]}>Resend Reset Email</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.secondaryButton, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}
            onPress={() => router.replace('/(auth)/signin')}>
            <Text style={[styles.secondaryButtonText, { color: theme.colors.textSecondary }]}>Back to Sign In</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (success) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.successContainer}>
          <View style={[styles.successIconContainer, { backgroundColor: theme.colors.surface, borderColor: '#4ADE80' }]}>
            <CheckCircle size={64} color="#4ADE80" />
          </View>
          <Text style={[styles.successTitle, { color: theme.colors.text }]}>Password Updated</Text>
          <Text style={styles.successMessage}>
            Your password has been successfully updated.
          </Text>
          <Text style={[styles.successSubtext, { color: theme.colors.textSecondary }]}>You can now sign in with your new password. Redirecting to sign in...</Text>
        </View>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <View style={styles.content}>
        <View style={[styles.iconContainer, { backgroundColor: theme.colors.surface, borderColor: theme.colors.accent }]}>
          <Lock size={48} color={theme.colors.accent} />
        </View>

        <Text style={[styles.title, { color: theme.colors.text }]}>Reset Password</Text>
        <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>Enter your new password below</Text>

        {error ? <Text style={[styles.errorText, { color: theme.colors.accent, backgroundColor: theme.colors.surface, borderColor: theme.colors.accent }]}>{error}</Text> : null}

        <View style={styles.form}>
          <View style={styles.inputContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>New Password</Text>
            <View style={[styles.passwordInputContainer, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
              <TextInput
                style={[styles.passwordInput, { color: theme.colors.text }]}
                value={password}
                onChangeText={text => {
                  setPassword(text);
                  setError('');
                }}
                placeholder="••••••••"
                placeholderTextColor={theme.colors.textTertiary}
                secureTextEntry={!showPassword}
                editable={!loading}
                autoFocus
              />
              <TouchableOpacity
                style={styles.eyeButton}
                onPress={() => setShowPassword(!showPassword)}>
                {showPassword ? (
                  <EyeOff size={20} color={theme.colors.textTertiary} />
                ) : (
                  <Eye size={20} color={theme.colors.textTertiary} />
                )}
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.inputContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>Confirm Password</Text>
            <View style={[styles.passwordInputContainer, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
              <TextInput
                style={[styles.passwordInput, { color: theme.colors.text }]}
                value={confirmPassword}
                onChangeText={text => {
                  setConfirmPassword(text);
                  setError('');
                }}
                placeholder="••••••••"
                placeholderTextColor={theme.colors.textTertiary}
                secureTextEntry={!showConfirmPassword}
                editable={!loading}
              />
              <TouchableOpacity
                style={styles.eyeButton}
                onPress={() => setShowConfirmPassword(!showConfirmPassword)}>
                {showConfirmPassword ? (
                  <EyeOff size={20} color={theme.colors.textTertiary} />
                ) : (
                  <Eye size={20} color={theme.colors.textTertiary} />
                )}
              </TouchableOpacity>
            </View>
          </View>

          <View style={[styles.requirements, { backgroundColor: theme.colors.background, borderColor: theme.colors.surface }]}>
            <Text style={[styles.requirementsTitle, { color: theme.colors.text }]}>Password Requirements:</Text>
            <Text style={[styles.requirement, { color: theme.colors.textTertiary }, password.length >= 8 && styles.requirementMet]}>
              • At least 8 characters
            </Text>
            <Text style={[styles.requirement, { color: theme.colors.textTertiary }, /[A-Za-z]/.test(password) && /[0-9]/.test(password) && styles.requirementMet]}>
              • Contains letters and numbers
            </Text>
            <Text
              style={[
                styles.requirement,
                { color: theme.colors.textTertiary },
                password === confirmPassword && password.length > 0 && styles.requirementMet,
              ]}>
              • Passwords match
            </Text>
          </View>

          <TouchableOpacity
            style={[styles.button, { backgroundColor: theme.colors.accent }, loading && styles.buttonDisabled]}
            onPress={handleResetPassword}
            disabled={loading}>
            {loading ? (
              <ActivityIndicator color={theme.colors.accentForeground} />
            ) : (
              <Text style={[styles.buttonText, { color: theme.colors.accentForeground }]}>Reset Password</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 32,
    paddingTop: 80,
    justifyContent: 'center',
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 4,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
    alignSelf: 'center',
  },
  title: {
    fontSize: 32,
    fontWeight: '900',
    marginBottom: 12,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 40,
    textAlign: 'center',
  },
  form: {
    gap: 24,
  },
  inputContainer: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
  },
  passwordInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 2,
  },
  passwordInput: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 16,
    fontSize: 16,
  },
  eyeButton: {
    padding: 16,
  },
  requirements: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
  },
  requirementsTitle: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 8,
  },
  requirement: {
    fontSize: 13,
    marginBottom: 4,
  },
  requirementMet: {
    color: '#4ADE80',
  },
  button: {
    paddingVertical: 18,
    borderRadius: 0,
    alignItems: 'center',
    marginTop: 8,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    fontSize: 18,
    fontWeight: '700',
  },
  errorText: {
    fontSize: 14,
    textAlign: 'center',
    padding: 12,
    borderRadius: 2,
    borderWidth: 1,
  },
  successContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  successIconContainer: {
    width: 120,
    height: 120,
    borderRadius: 4,
    borderWidth: 3,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 32,
  },
  successTitle: {
    fontSize: 28,
    fontWeight: '900',
    marginBottom: 16,
    textAlign: 'center',
  },
  successMessage: {
    fontSize: 16,
    color: '#4ADE80',
    marginBottom: 12,
    textAlign: 'center',
    fontWeight: '600',
  },
  successSubtext: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 22,
  },
  loadingText: {
    fontSize: 16,
    marginTop: 20,
    textAlign: 'center',
  },
  errorIconContainer: {
    width: 120,
    height: 120,
    borderRadius: 4,
    borderWidth: 3,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 32,
  },
  errorTitle: {
    fontSize: 28,
    fontWeight: '900',
    marginBottom: 16,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
    fontWeight: '600',
    lineHeight: 24,
    paddingHorizontal: 20,
  },
  fallbackInstructions: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 24,
    width: '100%',
  },
  fallbackTitle: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 8,
  },
  fallbackText: {
    fontSize: 14,
    lineHeight: 22,
  },
  secondaryButton: {
    paddingVertical: 16,
    borderRadius: 0,
    alignItems: 'center',
    borderWidth: 1,
    marginTop: 12,
    width: '100%',
  },
  secondaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  mobileHandoffBox: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 16,
    width: '100%',
  },
  mobileHandoffHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  mobileHandoffTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#4ADE80',
  },
  mobileHandoffText: {
    fontSize: 14,
    lineHeight: 20,
  },
  appLinkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    borderRadius: 0,
    marginBottom: 16,
    width: '100%',
  },
});
